// One keyboard and focus model for every dialog: the modal frame, the
// error modal, and the confirm dialog. Escape and Tab act on the topmost
// open dialog alone, and a dialog hands focus back to whatever opened it,
// even when another dialog replaced it in the same swap.

const focusable =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])';

// Open dialogs, oldest first.
const stack = [];
let listening = false;
let observer = null;

// The control behind the latest request a person started, for a dialog
// whose opener lost focus before it arrived: a menu item whose menu
// closed, or a button Safari never focused on click. Polls start
// requests too, and never count.
let lastTrigger = null;
document.addEventListener("htmx:beforeRequest", (event) => {
  const started = event.detail.requestConfig?.triggeringEvent;
  if (!started?.isTrusted) return;

  lastTrigger = started.submitter || event.detail.elt;
});

const live = (entry) =>
  entry.dialog.isConnected && !entry.dialog.classList.contains("hidden");

function topmost() {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (live(stack[i])) return stack[i];
  }

  return null;
}

function forget(entry) {
  const at = stack.indexOf(entry);
  if (at !== -1) stack.splice(at, 1);
}

function focusInto(entry) {
  const { dialog } = entry;
  const target =
    (entry.initialFocus?.isConnected && entry.initialFocus) ||
    dialog.querySelector("[autofocus]") ||
    dialog;
  if (target === dialog && !dialog.hasAttribute("tabindex")) {
    dialog.setAttribute("tabindex", "-1");
  }

  target.focus({ preventScroll: true });
}

// A dialog opened from a menu item can find the menu closed, its item no
// longer focusable, so focus goes back to the menu's own toggle. Focus
// that sat in a dialog since removed goes where that dialog's would, and
// focus with nowhere to go stays in the dialog still open.
function restore(entry) {
  const to = entry.returnTo;
  if (to instanceof HTMLElement && to.isConnected) {
    to.focus();

    if (document.activeElement !== to) {
      to.closest("details")?.querySelector("summary")?.focus();
    }
  } else if (entry.within) {
    restore(entry.within);

    return;
  }

  const open = topmost();
  if (open && !open.dialog.contains(document.activeElement)) focusInto(open);
}

function returnTarget() {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    active !== document.body &&
    active.isConnected
  ) {
    return active;
  }

  if (lastTrigger instanceof HTMLElement && lastTrigger.isConnected) {
    return lastTrigger;
  }

  return null;
}

// Focus in another overlay, such as a popover outside the dialog's own
// markup, is left alone; focus on the page or the dialog's frame comes
// back inside.
function trapTab(entry, event) {
  const { dialog } = entry;
  const active = document.activeElement;
  const astray = !active || active === document.body || active === dialog;
  if (!dialog.contains(active) && !astray) return;

  const controls = Array.from(dialog.querySelectorAll(focusable)).filter(
    (el) => el.getClientRects().length > 0,
  );

  if (controls.length === 0) {
    event.preventDefault();

    return;
  }

  const first = controls[0];
  const last = controls[controls.length - 1];
  const at = controls.indexOf(active);

  if (at === -1) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && at === 0) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && at === controls.length - 1) {
    event.preventDefault();
    first.focus();
  }
}

// A dialog that claims Escape takes it in the capture phase, before
// anything underneath sees it. Others close in the bubble phase, after a
// control inside has had the chance to mark the key handled.
function listen() {
  if (listening) return;

  listening = true;

  window.addEventListener(
    "keydown",
    (event) => {
      const entry = topmost();
      if (!entry) return;

      if (event.key === "Tab") {
        trapTab(entry, event);
      } else if (event.key === "Escape" && entry.claimsEscape) {
        event.preventDefault();
        event.stopPropagation();
        entry.close();
      }
    },
    true,
  );

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;

    const entry = topmost();
    if (entry && !entry.claimsEscape) entry.close();
  });
}

// A dialog removed by anything but its own close, an inline close button
// or a swap, still hands focus back. One that another dialog replaced
// hands the newer dialog what it would have handed back instead, and
// leaves focus where the newer one puts it.
function watchRemovals() {
  if (observer) return;

  observer = new MutationObserver(() => {
    for (const entry of stack.slice()) {
      if (entry.dialog.isConnected) continue;

      forget(entry);

      const heir = stack.find((other) => other.within === entry);
      if (heir) {
        heir.returnTo = entry.returnTo;
        heir.within = entry.within;

        continue;
      }

      queueMicrotask(() => {
        const active = document.activeElement;
        if (!active || active === document.body) restore(entry);
      });
    }

    if (stack.length === 0) {
      observer.disconnect();
      observer = null;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// A swap inside an open dialog, such as a step that replaces the button
// just pressed, can leave focus nowhere; it goes back into the dialog.
document.addEventListener("htmx:afterSettle", (event) => {
  const entry = topmost();
  const active = document.activeElement;
  const lost = !active || active === document.body || !active.isConnected;
  if (entry && lost && entry.dialog.contains(event.target)) focusInto(entry);
});

// openDialog registers dialog as the topmost and moves focus into it:
// to initialFocus, an [autofocus] control, or the dialog itself. close
// dismisses it; claimsEscape keeps Escape from reaching anything
// underneath; returnTo names the opener when the caller knows it. The
// handle's close runs close and hands focus back; release forgets the
// dialog without either, for an accept that moves on.
export function openDialog(
  dialog,
  {
    close = () => dialog.remove(),
    initialFocus = null,
    claimsEscape = false,
    returnTo = returnTarget(),
  } = {},
) {
  listen();
  watchRemovals();

  // An htmx swap inserts the new dialog before it removes the old, so
  // focus can still sit in the dialog this one replaces; within
  // remembers it.
  const within =
    stack.findLast((entry) => entry.dialog.contains(returnTo)) || null;

  for (const entry of stack.slice()) {
    if (entry.dialog === dialog || !entry.dialog.isConnected) forget(entry);
  }

  const entry = {
    dialog,
    claimsEscape,
    initialFocus,
    returnTo,
    within,
    close: () => {
      forget(entry);
      close();
      restore(entry);
    },
  };
  stack.push(entry);

  // A dialog's script can run before its children parse, and a form's
  // own script may focus a field as it renders, so focus waits a frame
  // and leaves a field that already took it.
  requestAnimationFrame(() => {
    if (!dialog.contains(document.activeElement)) focusInto(entry);
  });

  return { close: entry.close, release: () => forget(entry) };
}

window.liferidaDialogs = { open: openDialog };
